import type { RetentionMode } from '@sentinel/shared';
import { Button, Panel, StateBadge } from '@sentinel/ui';
import { formatDateTime, QueryState } from '@sentinel/web-core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlatformLastRun } from './PlatformLastRun';
import { PlatformRetentionModal } from './PlatformRetentionModal';
import { usePlatformRetention } from './use-platform-retention';

const K = 'console.settings.retention';
const MODE_TONE = { dry_run: 'info', active: 'ok' } as const;

/** The platform's own retention run (ended sessions, console trail): preview until the platform
 *  owner switches it on, with what the last run did or would do. */
export function PlatformRetentionPanel() {
  const { t } = useTranslation();
  const { overview, setMode } = usePlatformRetention();
  const [switching, setSwitching] = useState<RetentionMode | null>(null);
  return (
    <Panel title={t(`${K}.title`)}>
      <div className="stack">
        <p className="note">{t(`${K}.lead`)}</p>
        <QueryState query={overview}>
          {(data) => {
            const next: RetentionMode = data.mode === 'active' ? 'dry_run' : 'active';
            return (
              <div className="stack">
                <p style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <StateBadge tone={MODE_TONE[data.mode]} label={t(`${K}.modes.${data.mode}`)} />
                  <span className="note">{t(`${K}.modeHint.${data.mode}`)}</span>
                </p>
                <p className="note">
                  {t(`${K}.source.${data.source}`, { date: formatDateTime(data.updatedAt) })}
                </p>
                <Button
                  size="sm"
                  {...(next === 'active' ? { variant: 'danger' as const } : {})}
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => setSwitching(next)}
                >
                  {t(`${K}.switch.${next}`)}
                </Button>
                <PlatformLastRun overview={data} />
              </div>
            );
          }}
        </QueryState>
      </div>
      <PlatformRetentionModal
        mode={switching}
        busy={setMode.isPending}
        onClose={() => setSwitching(null)}
        onConfirm={(mode) => setMode.mutate(mode, { onSuccess: () => setSwitching(null) })}
      />
    </Panel>
  );
}
